/**
 * Zodiac & MBTI compatibility system.
 *
 * Shared between frontend (initial CP calculation) and backend (prompt injection).
 *
 * Used for:
 * 1. Adjusting initial CP (numerical)
 * 2. Injecting compatibility descriptions into prompts (behavioral)
 */
import type { Zodiac, MBTI } from '../../src/types';

// =============================================================================
// Zodiac element groups
// =============================================================================

type Element = 'fire' | 'earth' | 'air' | 'water';

const ZODIAC_ELEMENT: Record<Zodiac, Element> = {
  '白羊座': 'fire', '狮子座': 'fire', '射手座': 'fire',
  '金牛座': 'earth', '处女座': 'earth', '摩羯座': 'earth',
  '双子座': 'air', '天秤座': 'air', '水瓶座': 'air',
  '巨蟹座': 'water', '天蝎座': 'water', '双鱼座': 'water',
};

// Good combos: fire+air, earth+water
const ELEMENT_HARMONY: Record<string, number> = {
  'fire+air': 5, 'air+fire': 5,
  'earth+water': 5, 'water+earth': 5,
  'fire+fire': 3, 'earth+earth': 3, 'air+air': 3, 'water+water': 3,
  'fire+water': -3, 'water+fire': -3,
  'earth+air': -3, 'air+earth': -3,
  'fire+earth': 0, 'earth+fire': 0,
  'air+water': 0, 'water+air': 0,
};

const ELEMENT_NAMES: Record<Element, string> = {
  fire: '火象', earth: '土象', air: '风象', water: '水象',
};

// Special pairs (override element-based score)
const SPECIAL_PAIRS: Record<string, { cp: number; desc: string }> = {
  '天蝎座+双鱼座': { cp: 8, desc: '天作之合：两个水象灵魂的深度共鸣' },
  '双鱼座+天蝎座': { cp: 8, desc: '天作之合：两个水象灵魂的深度共鸣' },
  '白羊座+天秤座': { cp: 6, desc: '互补吸引：冲动与优雅的碰撞' },
  '天秤座+白羊座': { cp: 6, desc: '互补吸引：冲动与优雅的碰撞' },
  '狮子座+水瓶座': { cp: 6, desc: '王者对决：两个都想当主角' },
  '水瓶座+狮子座': { cp: 6, desc: '王者对决：两个都想当主角' },
  '金牛座+天蝎座': { cp: -5, desc: '冤家路窄：执念碰执念，互不退让' },
  '天蝎座+金牛座': { cp: -5, desc: '冤家路窄：执念碰执念，互不退让' },
  '双子座+处女座': { cp: -4, desc: '鸡同鸭讲：一个天马行空，一个锱铢必较' },
  '处女座+双子座': { cp: -4, desc: '鸡同鸭讲：一个天马行空，一个锱铢必较' },
};

export function calculateZodiacCompatibility(a: Zodiac, b: Zodiac): number {
  const specialKey = `${a}+${b}`;
  if (SPECIAL_PAIRS[specialKey]) return SPECIAL_PAIRS[specialKey].cp;

  const elA = ZODIAC_ELEMENT[a];
  const elB = ZODIAC_ELEMENT[b];
  return ELEMENT_HARMONY[`${elA}+${elB}`] ?? 0;
}

export function getZodiacCompatibilityDesc(a: Zodiac, b: Zodiac): string {
  const specialKey = `${a}+${b}`;
  if (SPECIAL_PAIRS[specialKey]) return SPECIAL_PAIRS[specialKey].desc;

  const elA = ZODIAC_ELEMENT[a];
  const elB = ZODIAC_ELEMENT[b];
  const score = ELEMENT_HARMONY[`${elA}+${elB}`] ?? 0;

  if (score >= 5) return `${ELEMENT_NAMES[elA]}+${ELEMENT_NAMES[elB]}：天然来电，气场相合`;
  if (score >= 3) return `同属${ELEMENT_NAMES[elA]}：容易理解彼此，但可能缺少新鲜感`;
  if (score <= -3) return `${ELEMENT_NAMES[elA]}+${ELEMENT_NAMES[elB]}：天生矛盾，需要磨合`;
  return `${ELEMENT_NAMES[elA]}+${ELEMENT_NAMES[elB]}：不好不坏，看个人造化`;
}

// =============================================================================
// MBTI compatibility (dimension-based)
// =============================================================================

// Classic "Golden Pairs" — complementary types that attract
const GOLDEN_PAIRS = new Set([
  'INTJ+ENFP', 'ENFP+INTJ', 'ENTJ+INFP', 'INFP+ENTJ',
  'INTP+ENFJ', 'ENFJ+INTP', 'ENTP+INFJ', 'INFJ+ENTP',
  'ISTJ+ESFP', 'ESFP+ISTJ', 'ESTJ+ISFP', 'ISFP+ESTJ',
  'ISTP+ESFJ', 'ESFJ+ISTP', 'ESTP+ISFJ', 'ISFJ+ESTP',
]);

function countDimensionDifferences(a: MBTI, b: MBTI): number {
  let diff = 0;
  for (let i = 0; i < 4; i++) {
    if (a[i] !== b[i]) diff++;
  }
  return diff;
}

export function calculateMBTICompatibility(a: MBTI, b: MBTI): number {
  if (GOLDEN_PAIRS.has(`${a}+${b}`)) return 5;
  if (a === b) return 0; // same type: understand each other, but boring
  const diff = countDimensionDifferences(a, b);
  if (diff >= 3) return -3; // too different: friction
  return 2; // moderate compatibility
}

// =============================================================================
// MBTI behavior hints (injected into chat prompts)
// =============================================================================

const MBTI_HINTS: Record<MBTI, string> = {
  INTJ: '独立思考、直接、不喜闲聊、有目标感',
  INTP: '逻辑至上、好奇心强、容易走神、讨厌无意义的寒暄',
  ENTJ: '天生领导者、控制欲强、效率优先、说话有气场',
  ENTP: '思维跳跃、喜欢辩论、爱抬杠、创意多但不落地',
  INFJ: '洞察力强、理想主义、话不多但说到要害、容易共情过度',
  INFP: '内心敏感、追求意义、文艺、容易受伤但不表现出来',
  ENFJ: '热情外向、善于读人、有感染力、可能过度干涉别人',
  ENFP: '天马行空、话题跳跃、热情洋溢、注意力容易分散',
  ISTJ: '一板一眼、可靠但无趣、重视规则、不善表达感情',
  ISFJ: '温柔体贴、默默付出、有点社恐、容易委屈自己',
  ESTJ: '雷厉风行、说一不二、爱教育人、实际但缺乏浪漫',
  ESFJ: '热心肠、八卦、在意别人看法、社交能力强',
  ISTP: '冷静寡言、动手能力强、讨厌废话、关键时刻靠谱',
  ISFP: '安静有品味、活在当下、不爱争论、有自己的小世界',
  ESTP: '胆大心细、行动派、爱冒险、坐不住、直来直去',
  ESFP: '天生表演者、爱热闹、话多、情绪外露、活力四射',
};

export function getMBTIBehaviorHint(mbti: MBTI): string {
  return MBTI_HINTS[mbti] || '性格复杂';
}

// =============================================================================
// Combined compatibility score for initial CP
// =============================================================================

export function calculateTotalCompatibility(
  zodiacA: Zodiac, zodiacB: Zodiac,
  mbtiA: MBTI, mbtiB: MBTI,
): number {
  return calculateZodiacCompatibility(zodiacA, zodiacB)
    + calculateMBTICompatibility(mbtiA, mbtiB);
}
